import React, { useState } from "react";
import { AlertCircle, CheckCircle, Loader, Send } from "lucide-react";
import { submitLead, validateLead } from "../../utils/lead";
import { trackFormLead } from "../../utils/ads";
import WhatsAppCta from "./WhatsAppCta";

/**
 * Formulario corto del hero.
 *
 * TRES CAMPOS, y son el mínimo real, no una elección de diseño: la tabla
 * omega_leads exige nombre y correo, y sin WhatsApp no hay con qué coordinar
 * una sesión. Todo lo demás — pack, volumen, rubro — se levanta en la
 * conversación o en el formulario largo de cada página de segmento.
 *
 * Tarjeta clara sobre el hero oscuro: es la misma regla del resto del sitio
 * (el texto que pide una acción va sobre papel) y además es lo que hace que se
 * vea como un formulario y no como decoración del fondo.
 *
 * `origen` propio para poder separar después, en la base de datos, los leads
 * que entran de golpe desde el hero de los que llegan tras leer una página de
 * segmento: son dos temperaturas distintas de cliente.
 */

const vacio = { name: "", whatsapp: "", email: "" };

const campo =
  "w-full rounded-xl border border-navy-200 bg-white px-4 py-3 text-navy-900 outline-none transition focus:border-navy-500";
const etiqueta = "mb-1.5 block text-xs font-semibold uppercase tracking-label text-navy-500";

const FotoLeadRapido = () => {
  const [datos, setDatos] = useState(vacio);
  const [estado, setEstado] = useState({ tipo: null, mensaje: "" });
  const [enviando, setEnviando] = useState(false);

  const cambiar = (event) => {
    const { name, value } = event.target;
    setDatos((actual) => ({ ...actual, [name]: value }));
    if (estado.tipo === "error") setEstado({ tipo: null, mensaje: "" });
  };

  const enviar = async (event) => {
    event.preventDefault();
    const error = validateLead(datos);
    if (error) {
      setEstado({ tipo: "error", mensaje: error });
      return;
    }

    setEnviando(true);
    setEstado({ tipo: null, mensaje: "" });

    try {
      await submitLead({ ...datos, origen: "hero-rapido" });
      trackFormLead({ form: "hero", pack: "sin_pack" });
      setEstado({
        tipo: "exito",
        mensaje: "Te escribimos por WhatsApp con disponibilidad y precio.",
      });
      setDatos(vacio);
    } catch (err) {
      console.error("Error al enviar el lead del hero:", err);
      setEstado({
        tipo: "error",
        mensaje: "No pudimos enviar el formulario. Escríbenos directo por WhatsApp.",
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white p-6 shadow-lift sm:p-7">
      {estado.tipo === "exito" ? (
        <div>
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sand-100">
            <CheckCircle className="h-6 w-6 text-navy-800" aria-hidden="true" />
          </span>
          <h2 className="mt-4 font-display text-xl font-bold tracking-tight text-navy-900">
            Recibimos tus datos.
          </h2>
          <p className="mt-2 leading-relaxed text-navy-600">{estado.mensaje}</p>
          <WhatsAppCta
            message="Hola Omega, acabo de dejar mis datos en la web."
            location="hero_form_exito"
            variant="dark"
            className="mt-5 w-full"
          >
            Seguir por WhatsApp
          </WhatsAppCta>
        </div>
      ) : (
        <>
          <h2 className="font-display text-xl font-bold tracking-tight text-navy-900">
            Pide fecha
          </h2>

          <form onSubmit={enviar} className="mt-5 space-y-4" noValidate>
            <label className="block">
              <span className={etiqueta}>Nombre</span>
              <input
                type="text"
                name="name"
                value={datos.name}
                onChange={cambiar}
                className={campo}
                placeholder="Tu nombre"
                autoComplete="name"
                required
              />
            </label>
            <label className="block">
              <span className={etiqueta}>WhatsApp</span>
              <input
                type="tel"
                name="whatsapp"
                value={datos.whatsapp}
                onChange={cambiar}
                className={campo}
                placeholder="+56 9 1234 5678"
                autoComplete="tel"
                required
              />
            </label>
            <label className="block">
              <span className={etiqueta}>Correo</span>
              <input
                type="email"
                name="email"
                value={datos.email}
                onChange={cambiar}
                className={campo}
                placeholder="tu@correo.cl"
                autoComplete="email"
                required
              />
            </label>

            {estado.tipo === "error" ? (
              <div
                className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-3"
                role="alert"
              >
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
                <p className="text-sm font-medium text-red-800">{estado.mensaje}</p>
              </div>
            ) : null}

            <button type="submit" disabled={enviando} className="btn-primary w-full">
              {enviando ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Enviando…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" aria-hidden="true" />
                  Quiero mi fecha
                </>
              )}
            </button>

            <p className="text-center text-xs leading-relaxed text-navy-500">
              Solo para coordinar el trabajo. Sin newsletter.
            </p>
          </form>
        </>
      )}
    </div>
  );
};

export default FotoLeadRapido;
